// 현금성자산 잔액관리
// 좌측: 계좌별 현재 잔액 관리 (입출금 이력 기반 현금잔액 계산)
// 우측: 현금성자산 잔액관리 기존 화면 (CMA 종목별 잔액)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  fetchBalanceHistory,
  createBalanceHistory,
  updateBalanceHistory,
  deleteBalanceHistory,
} from '../../api/balanceHistory';
import {
  fetchAccountSummary,
  fetchCashHistory,
  createCashHistory,
  updateCashHistory,
  deleteCashHistory,
} from '../../api/balance';
import { fetchAccounts } from '../../api/accounts';
import { fetchStocks } from '../../api/stocks';
import './BalanceHistoryPage.css';

const CUR_YEAR = new Date().getFullYear();
const TODAY    = new Date().toISOString().slice(0, 10);
const YEAR_OPTS = [];
for (let y = 2020; y <= 2030; y++) YEAR_OPTS.push(y);
const BALANCE_TYPES = ['이월잔액', '입금', '출금', '기타수익', '기타손실'];
const EMPTY_FORM = { account_name: '', ticker: '', balance_date: TODAY, balance_amount: '', memo: '' };

const fNum = (v) => (v != null && v !== '' ? Number(v).toLocaleString('ko-KR') : '');
const fInt = (v) => (v == null || v === undefined) ? '-' : Math.round(v).toLocaleString();

const BalanceHistoryPage = ({ isActive }) => {
  // ── 분할 패널 ─────────────────────────────────────────────────
  const [leftPct, setLeftPct] = useState(60);
  const containerRef = useRef(null);

  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    const totalW = containerRef.current?.getBoundingClientRect().width || 1000;
    const startX  = e.clientX;
    const startPct = leftPct;
    const onMove = (ev) => {
      const delta = ((ev.clientX - startX) / totalW) * 100;
      setLeftPct(Math.min(70, Math.max(30, startPct + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── 좌측 패널 상태 ────────────────────────────────────────────
  const [year,            setYear]            = useState(CUR_YEAR);
  const [searchYear,      setSearchYear]      = useState(CUR_YEAR);
  const [summaryList,     setSummaryList]     = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editRows,        setEditRows]        = useState([]);
  const [deletedIds,      setDeletedIds]      = useState([]);
  const [summaryLoading,  setSummaryLoading]  = useState(false);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [leftMsg,         setLeftMsg]         = useState({ text: '', type: '' });
  const [amtFocusKey,     setAmtFocusKey]     = useState(null);
  const [amtFocusStr,     setAmtFocusStr]     = useState('');
  const nextKeyRef = useRef(0);

  // ── 우측 패널 상태 (기존) ─────────────────────────────────────
  const [filterAccount,  setFilterAccount]  = useState('');
  const [filterTicker,   setFilterTicker]   = useState('');
  const [accounts,       setAccounts]       = useState([]);
  const [cmaStocks,      setCmaStocks]      = useState([]);
  const [gridList,       setGridList]       = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [message,        setMessage]        = useState({ text: '', type: '' });
  const [sortConfig,     setSortConfig]     = useState(null);
  const [popup,          setPopup]          = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [amtFocusR,      setAmtFocusR]      = useState(false);
  const [savingRight,    setSavingRight]    = useState(false);
  const [confirmItem,    setConfirmItem]    = useState(null);
  const [stockNameWidth, setStockNameWidth] = useState(180);
  const [memoWidth,      setMemoWidth]      = useState(200);
  const resizingRef = useRef(null);

  // ── 공통 유틸 ─────────────────────────────────────────────────
  const showLeftMsg = (text, type = 'success') => {
    setLeftMsg({ text, type });
    setTimeout(() => setLeftMsg({ text: '', type: '' }), 3000);
  };
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };
  const msgCls = (m) =>
    !m.text ? '' : m.type === 'success' ? 'msg-success' : m.type === 'error' ? 'msg-error' : 'msg-info';

  // ── 좌측: 데이터 로드 ─────────────────────────────────────────
  const doLoadSummary = async (y) => {
    setSummaryLoading(true);
    try {
      console.log('[잔액] 요약 조회 연도:', y);
      const data = await fetchAccountSummary(y);
      console.log('[잔액] 요약 결과:', data);
      setSummaryList(data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || '알 수 없는 오류';
      console.error('[잔액] 요약 조회 오류:', err);
      showLeftMsg(`계좌별 잔액 조회 실패: ${detail}`, 'error');
    } finally {
      setSummaryLoading(false);
    }
  };

  const doLoadHistory = async (accName, y) => {
    if (!accName) { setEditRows([]); setDeletedIds([]); return; }
    setHistoryLoading(true);
    try {
      const data = await fetchCashHistory(accName, y);
      setEditRows(data.map(r => ({
        _key: String(r.id),
        id: r.id,
        balance_date: r.balance_date,
        balance_type: r.balance_type,
        amount: r.amount,
        memo: r.memo || '',
        _orig: { balance_date: r.balance_date, balance_type: r.balance_type, amount: r.amount, memo: r.memo || '' },
      })));
      setDeletedIds([]);
    } catch {
      showLeftMsg('입출금 내역 조회 실패.', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── 좌측: 이벤트 핸들러 ──────────────────────────────────────
  const handleYearSearch = () => {
    const y = year;
    setSearchYear(y);
    setSelectedAccount(null);
    setEditRows([]);
    setDeletedIds([]);
    doLoadSummary(y);
  };

  const handleAccountClick = (accName) => {
    setSelectedAccount(accName);
    doLoadHistory(accName, searchYear);
  };

  const handleAddRow = () => {
    const k = `new-${nextKeyRef.current++}`;
    setEditRows(prev => [...prev, {
      _key: k, id: null, balance_date: TODAY, balance_type: '입금', amount: 0, memo: '', _orig: null,
    }]);
  };

  const handleRowChange = (key, field, value) =>
    setEditRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  const handleDeleteRow = (key, id) => {
    setEditRows(prev => prev.filter(r => r._key !== key));
    if (id !== null) setDeletedIds(prev => [...prev, id]);
  };

  const handleSave = async () => {
    if (!selectedAccount) return;
    setSaving(true);
    try {
      for (const id of deletedIds)
        await deleteCashHistory(id);

      for (const row of editRows.filter(r => r.id === null))
        await createCashHistory({
          account_name: selectedAccount,
          balance_date: row.balance_date,
          balance_type: row.balance_type,
          amount: Number(row.amount),
          memo: row.memo || '',
        });

      for (const row of editRows.filter(r => r.id !== null)) {
        const o = row._orig;
        const dirty = !o
          || row.balance_date !== o.balance_date
          || row.balance_type !== o.balance_type
          || Number(row.amount) !== o.amount
          || (row.memo || '') !== (o.memo || '');
        if (dirty)
          await updateCashHistory(row.id, {
            balance_date: row.balance_date,
            balance_type: row.balance_type,
            amount: Number(row.amount),
            memo: row.memo || '',
          });
      }

      showLeftMsg('저장되었습니다.');
      await doLoadHistory(selectedAccount, searchYear);
      await doLoadSummary(searchYear);
    } catch (err) {
      showLeftMsg(err.response?.data?.detail || '저장 실패.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── 좌측: 실시간 미리보기 ─────────────────────────────────────
  const previewPatch = useMemo(() => {
    if (!selectedAccount) return null;
    const base = summaryList.find(s => s.account_name === selectedAccount);
    if (!base) return null;
    const sum = (type) => editRows.filter(r => r.balance_type === type).reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const carryover    = sum('이월잔액');
    const deposit      = sum('입금');
    const withdrawal   = sum('출금');
    const other_income = sum('기타수익');
    const other_loss   = sum('기타손실');
    return {
      carryover,
      deposit,
      withdrawal,
      other_income,
      other_loss,
      cash_balance: carryover + deposit + base.sell_amount + base.dividend + other_income
                    - withdrawal - base.buy_amount - other_loss,
    };
  }, [editRows, selectedAccount, summaryList]);

  const displaySummary = useMemo(() =>
    summaryList.map(s =>
      s.account_name === selectedAccount && previewPatch ? { ...s, ...previewPatch } : s
    ),
  [summaryList, selectedAccount, previewPatch]);

  // ── 우측: 기존 핸들러 ─────────────────────────────────────────
  const latestMap = useMemo(() => {
    const map = {};
    gridList.forEach(r => {
      const k = `${r.account_name}|||${r.ticker}`;
      if (!map[k] || r.balance_date > map[k]) map[k] = r.balance_date;
    });
    return map;
  }, [gridList]);

  const isLatest = (row) =>
    latestMap[`${row.account_name}|||${row.ticker}`] === row.balance_date;

  const handleSort = (key) =>
    setSortConfig(prev =>
      prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  const sortArrow = (key) =>
    sortConfig?.key === key ? (sortConfig.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕';

  const displayRows = useMemo(() => {
    if (!sortConfig) return gridList;
    const { key, dir } = sortConfig;
    return [...gridList].sort((a, b) => {
      let va = a[key] ?? '', vb = b[key] ?? '';
      if (key === 'balance_amount') { va = Number(va); vb = Number(vb); }
      else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
      return dir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
  }, [gridList, sortConfig]);

  const tickerOptions = useMemo(() => {
    if (!filterAccount) return cmaStocks;
    const tickers = new Set(gridList.map(r => r.ticker));
    const from = cmaStocks.filter(s => tickers.has(s.ticker));
    return from.length > 0 ? from : cmaStocks;
  }, [filterAccount, gridList, cmaStocks]);

  const popupTickerOptions = useMemo(() => {
    if (!form.account_name) return cmaStocks;
    const tickers = new Set(gridList.filter(r => r.account_name === form.account_name).map(r => r.ticker));
    const from = cmaStocks.filter(s => tickers.has(s.ticker));
    return from.length > 0 ? from : cmaStocks;
  }, [form.account_name, gridList, cmaStocks]);

  const openCreate = () => { setForm({ ...EMPTY_FORM }); setAmtFocusR(false); setPopup({ mode: 'create', item: null }); };
  const openEdit = (item) => {
    setForm({ account_name: item.account_name, ticker: item.ticker, balance_date: item.balance_date,
              balance_amount: String(item.balance_amount), memo: item.memo || '' });
    setAmtFocusR(false); setPopup({ mode: 'edit', item });
  };
  const closePopup = () => { setPopup(null); setSavingRight(false); };

  const handleRightSave = async () => {
    if (!form.account_name)  { showMessage('계좌를 선택해주세요.', 'error'); return; }
    if (!form.ticker)        { showMessage('종목을 선택해주세요.', 'error'); return; }
    if (!form.balance_date)  { showMessage('기준일자를 입력해주세요.', 'error'); return; }
    const amount = parseFloat(String(form.balance_amount).replace(/,/g, ''));
    if (isNaN(amount))       { showMessage('잔액을 입력해주세요.', 'error'); return; }
    setSavingRight(true);
    try {
      const payload = { account_name: form.account_name, ticker: form.ticker,
                        balance_date: form.balance_date, balance_amount: amount, memo: form.memo || null };
      const res = popup.mode === 'create'
        ? await createBalanceHistory(payload)
        : await updateBalanceHistory(popup.item.id, { balance_date: form.balance_date, balance_amount: amount, memo: form.memo || null });
      showMessage(res.message || '저장되었습니다.');
      closePopup();
      await loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setSavingRight(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmItem) return;
    try {
      const res = await deleteBalanceHistory(confirmItem.id);
      showMessage(res.message || '삭제되었습니다.');
      setConfirmItem(null);
      await loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
      setConfirmItem(null);
    }
  };

  const startResize = (e, field, currentW) => {
    e.preventDefault(); e.stopPropagation();
    resizingRef.current = { field, startX: e.clientX, startW: currentW };
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const newW = Math.max(80, resizingRef.current.startW + ev.clientX - resizingRef.current.startX);
      if (resizingRef.current.field === 'stock_name') setStockNameWidth(newW);
      else if (resizingRef.current.field === 'memo')  setMemoWidth(newW);
    };
    const onUp = () => { resizingRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const displayAmountR = amtFocusR
    ? String(form.balance_amount).replace(/,/g, '')
    : (form.balance_amount !== '' ? fNum(Number(String(form.balance_amount).replace(/,/g, ''))) : '');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAccount) params.account_name = filterAccount;
      if (filterTicker)  params.ticker       = filterTicker;
      const res = await fetchBalanceHistory(params);
      setGridList(res.data || []);
    } catch {
      showMessage('조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterAccount, filterTicker]);

  const handleSearch = () => { setSortConfig(null); loadData(); };

  // ── 초기 로드 ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(setAccounts).catch(() => {});
    fetchStocks({ stock_type: 'CMA' }).then(setCmaStocks).catch(() => {});
    doLoadSummary(CUR_YEAR);
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // ── 렌더 ─────────────────────────────────────────────────────
  return (
    <>
      <div className="bhp-split-root" ref={containerRef}>

        {/* ═══════════════════ 좌측 패널 ═══════════════════ */}
        <div className="bhp-panel" style={{ width: `${leftPct}%` }}>

          <div className="bhp-panel-title-bar">
            <h2>계좌별 현재 잔액 관리</h2>
          </div>

          <div className="bhp-panel-toolbar">
            <label className="bh-label">연도</label>
            <select className="bh-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTS.map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <button className="btn btn-primary" onClick={handleYearSearch} disabled={summaryLoading}>
              조회
            </button>
            <span style={{ fontSize: '12px', color: '#888888' }}>
              현금잔액 = 이월잔액 + 입금 + 매도금액 + 분배금 + 기타수익 - 출금 - 매수금액 - 기타손실&nbsp;&nbsp;|&nbsp;&nbsp;현 투자원금 = 현재 미매도 매수원금
            </span>
          </div>

          <div className="bhp-panel-body">

            {/* ── 상단: 계좌별 잔액 요약 그리드 ── */}
            <div className="bhp-summary-section">
              <div className="grid-wrapper bhp-summary-wrapper">
                <table className="grid-table" style={{ width: '100%', tableLayout: 'fixed', fontSize: '12px' }}>
                  <colgroup>
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '85px' }} />
                    <col style={{ width: '85px' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#F5F5F5', textAlign: 'right' }}>
                      <th style={{ textAlign: 'left' }}>계좌명</th>
                      <th>이월잔액</th>
                      <th>입금</th>
                      <th>출금</th>
                      <th>매도금액</th>
                      <th>매수금액</th>
                      <th>분배금</th>
                      <th style={{ color: '#2255CC' }}>기타수익</th>
                      <th style={{ color: '#FF4444' }}>기타손실</th>
                      <th style={{ color: '#000000', fontWeight: 'bold' }}>현금잔액</th>
                      <th style={{ color: '#884400' }}>현 투자원금</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryLoading ? (
                      <tr><td colSpan={11} className="grid-empty">조회 중...</td></tr>
                    ) : displaySummary.length === 0 ? (
                      <tr><td colSpan={11} className="grid-empty">데이터가 없습니다.</td></tr>
                    ) : displaySummary.map(s => (
                      <tr
                        key={s.account_name}
                        onClick={() => handleAccountClick(s.account_name)}
                        style={{
                          cursor: 'pointer',
                          background: selectedAccount === s.account_name ? '#E8F0FE' : 'white',
                          textAlign: 'right',
                        }}
                      >
                        <td style={{ textAlign: 'left', fontWeight: '500' }}>{s.account_name}</td>
                        <td>{fInt(s.carryover)}</td>
                        <td>{fInt(s.deposit)}</td>
                        <td>{fInt(s.withdrawal)}</td>
                        <td>{fInt(s.sell_amount)}</td>
                        <td>{fInt(s.buy_amount)}</td>
                        <td>{fInt(s.dividend)}</td>
                        <td style={{ color: '#2255CC' }}>{fInt(s.other_income)}</td>
                        <td style={{ color: '#FF4444' }}>{fInt(s.other_loss)}</td>
                        <td style={{ fontWeight: 'bold', color: '#000000' }}>{fInt(s.cash_balance)}</td>
                        <td style={{ color: '#884400', fontWeight: '500' }}>{fInt(s.invest_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── 하단: 계좌별 입출금 내역 그리드 ── */}
            <div className="bhp-history-section">
              <div className="bhp-history-header">
                <span className="bhp-history-label">
                  {selectedAccount ? `${selectedAccount} 입출금 내역` : '↑ 계좌를 클릭하세요'}
                </span>
                <div className="bhp-history-btns">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleAddRow}
                    disabled={!selectedAccount}
                  >
                    행 추가
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={!selectedAccount || saving}
                  >
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>

              <div className="grid-wrapper bhp-history-wrapper">
                <table className="grid-table" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '120px' }} />
                    <col />
                    <col style={{ width: '50px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>일자</th>
                      <th>입출구분</th>
                      <th className="col-right">금액</th>
                      <th>비고</th>
                      <th className="col-center">삭제</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedAccount ? (
                      <tr><td colSpan={5} className="grid-empty">계좌를 선택하세요.</td></tr>
                    ) : historyLoading ? (
                      <tr><td colSpan={5} className="grid-empty">조회 중...</td></tr>
                    ) : editRows.length === 0 ? (
                      <tr><td colSpan={5} className="grid-empty">[행 추가]를 클릭하여 등록하세요.</td></tr>
                    ) : editRows.map(row => (
                      <tr key={row._key}>
                        <td>
                          <input
                            type="date"
                            className="bhp-cell-date"
                            value={row.balance_date}
                            onChange={(e) => handleRowChange(row._key, 'balance_date', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="bhp-cell-select"
                            value={row.balance_type}
                            onChange={(e) => handleRowChange(row._key, 'balance_type', e.target.value)}
                          >
                            {BALANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="bhp-cell-amt"
                            value={row._key === amtFocusKey ? amtFocusStr : fNum(row.amount)}
                            onFocus={() => {
                              setAmtFocusKey(row._key);
                              setAmtFocusStr(String(row.amount || 0));
                            }}
                            onBlur={() => {
                              const n = parseInt(amtFocusStr.replace(/[^0-9]/g, '') || '0');
                              handleRowChange(row._key, 'amount', n);
                              setAmtFocusKey(null);
                              setAmtFocusStr('');
                            }}
                            onChange={(e) => setAmtFocusStr(e.target.value.replace(/[^0-9]/g, ''))}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="bhp-cell-memo"
                            value={row.memo || ''}
                            onChange={(e) => handleRowChange(row._key, 'memo', e.target.value)}
                            placeholder="비고"
                            maxLength={200}
                          />
                        </td>
                        <td className="col-center">
                          <button
                            className="btn-icon btn-icon-del"
                            title="삭제"
                            onClick={() => handleDeleteRow(row._key, row.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={`page-message-bar ${msgCls(leftMsg)}`}>{leftMsg.text || ' '}</div>
        </div>

        {/* ═══════════════════ 구분선 ═══════════════════ */}
        <div className="bhp-divider" onMouseDown={handleDividerMouseDown} />

        {/* ═══════════════════ 우측 패널 (기존) ═══════════════════ */}
        <div className="bhp-panel" style={{ width: `calc(${100 - leftPct}% - 6px)` }}>

          <div className="bhp-panel-title-bar">
            <h2>현금성자산 잔액관리</h2>
          </div>

          <div className="bhp-panel-toolbar">
            <label className="bh-label">계좌</label>
            <select
              className="bh-select"
              value={filterAccount}
              onChange={(e) => { setFilterAccount(e.target.value); setFilterTicker(''); }}
            >
              <option value="">전체</option>
              {accounts.map(a => <option key={a.id} value={a.account_name}>{a.account_name}</option>)}
            </select>
            <label className="bh-label">종목</label>
            <select
              className="bh-select"
              value={filterTicker}
              onChange={(e) => setFilterTicker(e.target.value)}
            >
              <option value="">전체</option>
              {tickerOptions.map(s => (
                <option key={s.ticker} value={s.ticker}>{s.stock_name} ({s.ticker})</option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>조회</button>
            <button className="btn btn-success" onClick={openCreate}>신규</button>
          </div>

          <div className="bhp-panel-body">
            <div style={{
              background: '#F0F7FF',
              border: '1px solid #C0D8F0',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '12px',
              fontSize: '12px',
              color: '#444444',
              lineHeight: '1.8'
            }}>
              <b>📌 현금성자산 잔액관리 안내</b><br/>
              이 영역은 계좌 내 세부 현금성자산
              (예금, CMA, IRP 이자 등)을
              티커별로 등록/관리합니다.<br/>
              등록된 자료는 <b>분배금등록</b>의
              현금성자산 항목에 활용됩니다.<br/>
              계좌 전체의 현금잔액은
              왼쪽 영역에서 확인하세요.
            </div>
            <div className="grid-wrapper">
              <table className="grid-table bh-grid">
                <thead>
                  <tr>
                    <th className="bh-sortable" style={{ width: 90 }} onClick={() => handleSort('account_name')}>
                      계좌명{sortArrow('account_name')}
                    </th>
                    <th className="bh-sortable bh-resizable-th" style={{ width: stockNameWidth }} onClick={() => handleSort('stock_name')}>
                      종목명{sortArrow('stock_name')}
                      <span className="bh-resize-handle" onMouseDown={(e) => startResize(e, 'stock_name', stockNameWidth)} onClick={(e) => e.stopPropagation()} />
                    </th>
                    <th className="bh-sortable col-center" style={{ width: 80 }} onClick={() => handleSort('ticker')}>
                      티커{sortArrow('ticker')}
                    </th>
                    <th className="bh-sortable" style={{ width: 100 }} onClick={() => handleSort('balance_date')}>
                      기준일자{sortArrow('balance_date')}
                    </th>
                    <th className="col-right" style={{ width: 120 }}>잔액</th>
                    <th className="col-center" style={{ width: 60 }}>수정</th>
                    <th className="col-center" style={{ width: 60 }}>삭제</th>
                    <th className="bh-resizable-th" style={{ width: memoWidth }}>
                      메모
                      <span className="bh-resize-handle" onMouseDown={(e) => startResize(e, 'memo', memoWidth)} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="grid-empty">
                        {loading ? '조회 중...' : '데이터가 없습니다.'}
                      </td>
                    </tr>
                  ) : displayRows.map(row => (
                    <tr key={row.id} className={isLatest(row) ? 'bh-row-latest' : ''}>
                      <td>{row.account_name}</td>
                      <td className="col-ellipsis" style={{ maxWidth: stockNameWidth }} title={row.stock_name || row.ticker}>
                        {row.stock_name || row.ticker}
                      </td>
                      <td className="col-center">{row.ticker}</td>
                      <td>{row.balance_date}</td>
                      <td className="col-right bh-amount">{fNum(row.balance_amount)}</td>
                      <td className="col-center">
                        <button className="btn-icon" onClick={() => openEdit(row)} title="수정">✏️</button>
                      </td>
                      <td className="col-center">
                        <button className="btn-icon btn-icon-del" onClick={() => setConfirmItem(row)} title="삭제">🗑️</button>
                      </td>
                      <td className="col-ellipsis bh-memo" style={{ maxWidth: memoWidth }} title={row.memo || ''}>
                        {row.memo || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`page-message-bar ${msgCls(message)}`}>{message.text || ' '}</div>
        </div>
      </div>

      {/* ── 우측 패널: 등록/수정 팝업 ── */}
      {popup && (
        <div className="bh-overlay" onClick={closePopup}>
          <div className="bh-popup" onClick={(e) => e.stopPropagation()}>
            <div className="bh-popup-header">
              <span>{popup.mode === 'create' ? '현금성자산 잔액 등록' : '현금성자산 잔액 수정'}</span>
              <button className="bh-popup-close" onClick={closePopup}>✕</button>
            </div>
            <div className="bh-popup-body">
              <div className="bh-form-row">
                <label className="bh-form-label">계좌 *</label>
                <select
                  className="bh-form-input"
                  value={form.account_name}
                  onChange={(e) => setForm(f => ({ ...f, account_name: e.target.value, ticker: '' }))}
                  disabled={popup.mode === 'edit'}
                >
                  <option value="">선택</option>
                  {accounts.map(a => <option key={a.id} value={a.account_name}>{a.account_name}</option>)}
                </select>
              </div>
              <div className="bh-form-row">
                <label className="bh-form-label">종목 *</label>
                <select
                  className="bh-form-input"
                  value={form.ticker}
                  onChange={(e) => setForm(f => ({ ...f, ticker: e.target.value }))}
                  disabled={popup.mode === 'edit'}
                >
                  <option value="">선택</option>
                  {popupTickerOptions.map(s => (
                    <option key={s.ticker} value={s.ticker}>{s.stock_name} ({s.ticker})</option>
                  ))}
                </select>
              </div>
              <div className="bh-form-row">
                <label className="bh-form-label">기준일자 *</label>
                <input
                  type="date"
                  className="bh-form-input"
                  value={form.balance_date}
                  onChange={(e) => setForm(f => ({ ...f, balance_date: e.target.value }))}
                />
              </div>
              <div className="bh-form-row">
                <label className="bh-form-label">잔액 *</label>
                <input
                  type="text"
                  className="bh-form-input bh-right"
                  value={displayAmountR}
                  onFocus={() => setAmtFocusR(true)}
                  onBlur={() => setAmtFocusR(false)}
                  onChange={(e) => setForm(f => ({ ...f, balance_amount: e.target.value.replace(/[^0-9.]/g, '') }))}
                  placeholder="0"
                />
              </div>
              <div className="bh-form-row">
                <label className="bh-form-label">메모</label>
                <input
                  type="text"
                  className="bh-form-input"
                  value={form.memo}
                  onChange={(e) => setForm(f => ({ ...f, memo: e.target.value }))}
                  maxLength={500}
                  placeholder="메모 입력"
                />
              </div>
            </div>
            <div className="bh-popup-footer">
              <button className="btn btn-primary" onClick={handleRightSave} disabled={savingRight}>
                {savingRight ? '저장 중...' : '저장'}
              </button>
              <button className="btn btn-secondary" onClick={closePopup}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 우측 패널: 삭제 확인 팝업 ── */}
      {confirmItem && (
        <div className="bh-overlay" onClick={() => setConfirmItem(null)}>
          <div className="bh-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="bh-confirm-msg">해당 잔액 이력을 삭제합니다. 계속하시겠습니까?</p>
            <p className="bh-confirm-detail">
              {confirmItem.account_name} / {confirmItem.stock_name || confirmItem.ticker} / {confirmItem.balance_date}
            </p>
            <div className="bh-confirm-btns">
              <button className="btn btn-danger" onClick={handleDeleteConfirm}>삭제</button>
              <button className="btn btn-secondary" onClick={() => setConfirmItem(null)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BalanceHistoryPage;
